import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QrScanButton } from '@/components/surveyAccess/QrScanButton';

export const entryDefaults = (brand) => ({
  heading: 'Welcome!',
  intro: `Welcome to the ${brand} Customer Experience Survey.\nYour feedback will help us improve the ${brand} experience.\nCheck your receipt to get started.`,
  qr_title: 'If your receipt has a QR code',
  qr_text: 'Tap Scan QR Code and scan the QR code printed on your receipt.',
  code_title: 'If your receipt has a Feedback Code / Survey ID',
  code_text: 'Enter the code printed on your receipt below.',
});

const Block = ({ title, text, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <div>
      <h2 className="text-base font-semibold text-[#0F172A]">{title}</h2>
      <p className="text-sm text-[#475569] mt-0.5">{text}</p>
    </div>
    {children}
  </section>
);

// Generic public entry access page (brand text comes from the survey's public_entry settings).
export const EntryAccessPanel = ({ settings, brandName, qrOn, manualOn, value, onValue, onContinue, onScanned, busy, primary }) => {
  const filled = Object.fromEntries(Object.entries(settings || {}).filter(([, v]) => typeof v === 'string' && v.trim()));
  const s = { ...entryDefaults(brandName || 'our'), ...filled };
  return (
    <div className="space-y-6" data-testid="entry-access-page">
      <div className="space-y-2">
        <h1 className="std2026-qtitle text-2xl" data-testid="entry-heading">{s.heading}</h1>
        <p className="text-sm text-[#334155] whitespace-pre-line leading-relaxed" data-testid="entry-intro">{s.intro}</p>
      </div>
      {qrOn && (
        <Block title={s.qr_title} text={s.qr_text} testid="entry-qr-section">
          <QrScanButton onScanned={onScanned} disabled={busy} primary={primary} />
        </Block>
      )}
      {qrOn && manualOn && (
        <div className="flex items-center gap-3 text-xs font-semibold tracking-widest text-[#94A3B8]" data-testid="entry-or">
          <span className="flex-1 h-px bg-[#E2E8F0]" /> OR <span className="flex-1 h-px bg-[#E2E8F0]" />
        </div>
      )}
      {manualOn && (
        <Block title={s.code_title} text={s.code_text} testid="entry-code-section">
          <div className="std2026-access">
            <label className="std2026-access-label" htmlFor="entry-code-input">Feedback Code</label>
            <input id="entry-code-input" className="std2026-access-input" value={value} onChange={(e) => onValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && value.trim() && !busy) onContinue(); }}
              placeholder="Enter the code printed on your receipt" inputMode="text" autoComplete="off" data-testid="entry-code-input" />
          </div>
          <Button type="button" onClick={onContinue} disabled={busy || !value.trim()} className="w-full text-white h-11"
            style={{ backgroundColor: primary }} data-testid="entry-continue-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
          </Button>
        </Block>
      )}
    </div>
  );
};
